import { useId } from 'react';
import { cn } from '@/lib/utils';
import { Toggle, Typography } from '@/shared-ui/atoms';

interface SearchResultToggleProps {
    itemCount: number;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    toggleId?: string;
    toggleLabel?: string;
    textLabel?: string;
    /** Localised count text (e.g. "199 Results"); defaults to English pluralisation. */
    countLabel?: string;
    /** Hide the switch (e.g. when the `enableInStockOnlySwitch` site preference is off) but keep the count. */
    showToggle?: boolean;
    className?: string;
    textClassName?: string;
}

export const SearchResultToggle = ({
    itemCount,
    checked,
    onCheckedChange,
    toggleId,
    toggleLabel,
    textLabel = 'In stock only',
    countLabel,
    showToggle = true,
    className,
    textClassName = 'text-body-sm',
}: SearchResultToggleProps) => {
    const generatedId = useId().replace(/:/g, '');
    const resolvedToggleId = toggleId ?? `search-result-toggle-${generatedId}`;
    const labelId = `${resolvedToggleId}-label`;

    return (
        <div className={cn('flex items-center justify-between', className)}>
            {showToggle ? (
                <div className="flex items-center gap-2">
                    <Typography as="span" id={labelId} className={cn('text-primary font-light', textClassName)}>
                        {textLabel}
                    </Typography>
                    <Toggle
                        id={resolvedToggleId}
                        variant="default"
                        checked={checked}
                        onCheckedChange={onCheckedChange}
                        aria-label={toggleLabel}
                        aria-labelledby={toggleLabel ? undefined : labelId}
                        hasLabel
                    />
                </div>
            ) : (
                <span />
            )}
            <Typography as="span" aria-live="polite" className={cn('text-primary font-light', textClassName)}>
                {countLabel ?? (itemCount === 1 ? `${itemCount} Result` : `${itemCount} Results`)}
            </Typography>
        </div>
    );
};
